<template>
  <div>
    <!-- ================= STEP 1: BILL DETAILS & UPLOAD ================= -->
    <div v-if="step === 1">
      <p class="text-muted">
        Bill students from any class or department for one month, from a sheet with one row per student and an
        amount. Each student is found by Register No or Admission No and billed on the
        <strong>fee structure they're already mapped to</strong> - that structure must include the component chosen
        below. Students already billed for it for overlapping dates are skipped.
      </p>

      <b-row>
        <b-col cols="12" md="6">
          <b-form-group label="Component" label-class="font-weight-bold">
            <b-form-select v-model="form.fee_component_id" :options="catalogOptions" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="Bill Name" label-class="font-weight-bold">
            <b-form-input v-model.trim="form.bill_name" placeholder="e.g. Mess Bill - Oct 2026" />
          </b-form-group>
        </b-col>
        <!-- Billing frequency comes from the component as set in the fee structures -->
        <b-col v-if="form.fee_component_id" cols="12" md="6">
          <b-form-group label="Billing Frequency" label-class="font-weight-bold"
            :description="componentFrequencies.length > 1
              ? 'Structures bill this component differently - students on other frequencies are skipped.' : ''">
            <div v-if="!componentFrequencies.length" class="text-danger small">
              No fee structure includes this component yet.
            </div>
            <b-form-select v-else-if="componentFrequencies.length > 1" v-model="form.billing_frequency"
              :options="componentFrequencies.map((f) => ({ value: f, text: frequencyLabel(f) }))" />
            <div v-else class="pt-2">{{ frequencyLabel(form.billing_frequency) }}</div>
          </b-form-group>
        </b-col>

        <b-col v-if="form.billing_frequency === 'monthly'" cols="12" md="6">
          <b-form-group label="Month" label-class="font-weight-bold"
            description="Fills in the start and end dates - change them for a different cycle.">
            <b-form-input v-model="form.month" type="month" @change="setDatesFromMonth" />
          </b-form-group>
        </b-col>
        <b-col v-if="form.billing_frequency === 'semester' || form.billing_frequency === 'yearly'" cols="12" md="6">
          <b-form-group label="Academic Year" label-class="font-weight-bold">
            <b-form-select v-model="form.academic_year" :options="$acedemicYears" />
          </b-form-group>
        </b-col>
        <b-col v-if="form.billing_frequency === 'semester'" cols="12" md="6">
          <b-form-group label="Semester" label-class="font-weight-bold">
            <b-form-select v-model="form.semester" :options="semesterOptions" />
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-group label="Due Date (Pay with Fine)" label-class="font-weight-bold">
            <b-form-input v-model="form.due_date" type="date" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="Start Date" label-class="font-weight-bold">
            <b-form-input v-model="form.start_date" type="date" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="End Date" label-class="font-weight-bold"
            :invalid-feedback="'End date must be on or after the start date.'" :state="datesValid ? null : false">
            <b-form-input v-model="form.end_date" type="date" :state="datesValid ? null : false" />
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-file v-model="uploadedFile" accept=".xlsx,.xls" size="sm" @change="onFileChange"
        placeholder="Choose the mess Excel file or drop it here..." drop-placeholder="Drop file here..." />

      <div v-if="rawRows.length" class="mt-3">
        <p class="mb-1"><strong>How many rows (from the top) are column headings?</strong></p>
        <b-form-input type="number" min="1" :max="rawRows.length" v-model.number="headerRowCount"
          style="max-width: 120px;" />

        <div class="table-responsive mt-3">
          <table class="table table-sm table-bordered">
            <tbody>
              <tr v-for="(row, idx) in rawRows.slice(0, 6)" :key="idx"
                :class="{ 'table-warning': idx + 1 <= headerRowCount }">
                <td class="text-muted">{{ idx + 1 }}</td>
                <td v-for="(cell, cIdx) in row" :key="cIdx">{{ cell }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="text-right mt-3">
        <b-button variant="primary" :disabled="!rawRows.length || !formComplete" @click="buildColumnMappings">
          Next
        </b-button>
      </div>

      <div v-if="recentBatches.length" class="mt-4">
        <h6>Recent Mess Imports</h6>
        <div class="table-responsive">
          <table class="table table-sm table-bordered">
            <thead class="thead-light">
              <tr>
                <th>When</th>
                <th>File</th>
                <th>Imported / Skipped</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="batch in recentBatches" :key="batch.id">
                <td>{{ formatDate(batch.created_at) }}</td>
                <td>{{ batch.file_name || '-' }}</td>
                <td>{{ batch.imported_rows }} / {{ batch.skipped_rows }}</td>
                <td>
                  <span v-if="batch.reverted_at" class="text-muted">Reverted</span>
                  <span v-else class="text-success">Active</span>
                </td>
                <td class="text-right">
                  <b-button v-if="!batch.reverted_at" variant="outline-danger" size="sm"
                    :disabled="revertingBatchId === batch.id" @click="revertBatch(batch)">
                    <b-spinner small v-if="revertingBatchId === batch.id" /> Revert
                  </b-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ================= STEP 2: MAP COLUMNS ================= -->
    <div v-else-if="step === 2">
      <p class="text-muted">
        Map one column as the student's Register No or Admission No, and one as the Amount. Everything else in the
        sheet (name, class, room, etc.) is ignored.
      </p>

      <div v-if="mappingError" class="alert alert-danger">{{ mappingError }}</div>

      <div class="table-responsive">
        <table class="table table-sm table-bordered align-middle">
          <thead class="thead-light">
            <tr>
              <th>Column in your sheet</th>
              <th style="width: 220px;">What is this?</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="mapping in columnMappings" :key="mapping.index">
              <td>{{ mapping.header }}</td>
              <td>
                <b-form-select v-model="mapping.type" :options="columnTypeOptions" class="form-control-sm" />
              </td>
              <td>
                <b-icon icon="x" style="cursor: pointer;" title="Remove this column from the import"
                  @click="removeColumnMapping(mapping)" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="excludedColumns.length" class="mt-2">
        <small class="text-muted mr-2">Not included (click to add back if one of these is actually needed):</small>
        <b-badge v-for="c in excludedColumns" :key="c.index" variant="light" class="mr-1" style="cursor: pointer;"
          @click="restoreColumn(c)">
          + {{ c.header }}
        </b-badge>
      </div>

      <div class="text-right mt-3">
        <b-button variant="outline-secondary" class="mr-2" @click="step = 1">Back</b-button>
        <b-button variant="primary" :disabled="loading" @click="runDryRun">
          <b-spinner small v-if="loading" /> Preview Import
        </b-button>
      </div>
    </div>

    <!-- ================= STEP 3: PREVIEW & CONFIRM ================= -->
    <div v-else-if="step === 3">
      <div v-if="previewSummary" class="mb-3">
        <span class="badge badge-success mr-2">{{ previewSummary.ok }} ready</span>
        <span class="badge badge-danger mr-2">{{ previewSummary.error }} will be skipped</span>
        <span class="text-muted">of {{ previewSummary.total }} row(s)</span>
      </div>

      <div v-if="importResult" class="alert alert-success">
        Import complete - {{ importResult.summary.imported }} row(s) imported,
        {{ importResult.summary.skipped }} skipped.
      </div>

      <div class="table-responsive">
        <table class="table table-sm table-bordered">
          <thead class="thead-light">
            <tr>
              <th>Row</th>
              <th>Student ID</th>
              <th>Student</th>
              <th>Fee Structure</th>
              <th class="text-right">Amount</th>
              <th>Status</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in previewRows" :key="row.row_index">
              <td>{{ row.row_index + 1 }}</td>
              <td>{{ row.identifier }}</td>
              <td>{{ row.student ? row.student.name : '-' }}</td>
              <td>{{ row.category_name || '-' }}</td>
              <td class="text-right">{{ row.amount }}</td>
              <td>
                <span :class="statusBadgeClass(row.status)">{{ row.status }}</span>
              </td>
              <td>
                <div v-for="(msg, i) in row.errors" :key="'e' + i" class="text-danger small">{{ msg }}</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="text-right mt-3">
        <template v-if="!importResult">
          <b-button variant="outline-secondary" class="mr-2" @click="step = 2">Back</b-button>
          <b-button variant="success" :disabled="loading || !previewSummary || previewSummary.ok === 0"
            @click="confirmImport">
            <b-spinner small v-if="loading" /> Import {{ previewSummary ? previewSummary.ok : '' }} Row(s)
          </b-button>
        </template>
        <b-button v-else variant="primary" @click="$emit('done')">Done</b-button>
      </div>
    </div>
  </div>
</template>

<script>
import readXlsxFile from "read-excel-file";
import moment from "moment";

/**
 * Monthly mess bill import - see FeeMessImportController. Kept separate from
 * the legacy balance import (FeeImport.vue): different columns, and it bills
 * each student's open ledger instead of migrating old balances.
 */
export default {
  name: "MessBillImport",
  data() {
    return {
      step: 1,
      loading: false,

      form: {
        fee_component_id: null,
        bill_name: "",
        billing_frequency: null,
        month: moment().format("YYYY-MM"),
        academic_year: null,
        semester: null,
        start_date: moment().startOf("month").format("YYYY-MM-DD"),
        end_date: moment().endOf("month").format("YYYY-MM-DD"),
        due_date: null,
      },
      catalogComponents: [],
      feeCategories: [],
      semesterOptions: [
        { value: null, text: "Select a semester" },
        { value: "odd", text: "Odd" },
        { value: "even", text: "Even" },
      ],

      uploadedFile: null,
      fileName: null,
      rawRows: [],
      dataRows: [],
      headerRowCount: 1,

      columnMappings: [],
      excludedColumns: [],
      mappingError: null,

      previewReport: null,
      importResult: null,

      recentBatches: [],
      revertingBatchId: null,

      columnTypeOptions: [
        { value: "ignore", text: "Ignore (not imported)" },
        { value: "register_no", text: "Register No" },
        { value: "admission_no", text: "Admission No" },
        { value: "amount", text: "Amount" },
      ],
    };
  },
  computed: {
    catalogOptions() {
      return [
        { value: null, text: "Select a component" },
        ...this.catalogComponents.map((component) => ({ value: component.id, text: component.name })),
      ];
    },
    // Every billing frequency the chosen component has across the structures.
    componentFrequencies() {
      const frequencies = new Set();
      this.feeCategories.forEach((category) => {
        category.components
          .filter((component) => component.fee_component_id === this.form.fee_component_id)
          .forEach((component) => frequencies.add(component.billing_frequency));
      });
      return [...frequencies];
    },
    datesValid() {
      return !this.form.start_date || !this.form.end_date || this.form.end_date >= this.form.start_date;
    },
    periodComplete() {
      const { billing_frequency: frequency, month, academic_year: academicYear, semester } = this.form;
      if (frequency === "monthly") return !!month;
      if (frequency === "semester") return !!(academicYear && semester);
      if (frequency === "yearly") return !!academicYear;
      return frequency === "once";
    },
    formComplete() {
      return !!(this.form.fee_component_id && this.form.bill_name && this.periodComplete
        && this.form.start_date && this.form.end_date && this.datesValid);
    },
    previewSummary() {
      return this.previewReport ? this.previewReport.summary : null;
    },
    previewRows() {
      return this.previewReport ? this.previewReport.rows : [];
    },
  },
  watch: {
    // Default to the component's frequency (the first, if structures differ).
    componentFrequencies(frequencies) {
      if (!frequencies.includes(this.form.billing_frequency)) {
        this.form.billing_frequency = frequencies[0] || null;
      }
    },
  },
  async created() {
    await Promise.all([this.loadRecentBatches(), this.loadFeeCategories(), this.loadCatalogComponents()]);
  },
  methods: {
    async loadFeeCategories() {
      const url = this.$store.getters.getAPIKey.mainAPI + `/feecategories`;
      const response = await this.$axios.get(url);
      this.feeCategories = response.data;
    },

    frequencyLabel(frequency) {
      return frequency ? frequency.charAt(0).toUpperCase() + frequency.slice(1) : "-";
    },

    async loadCatalogComponents() {
      const url = this.$store.getters.getAPIKey.mainAPI + `/feecomponents/catalog`;
      const response = await this.$axios.get(url);
      this.catalogComponents = response.data;
      // Pre-pick "Mess" if the catalog has it.
      const mess = this.catalogComponents.find((component) => /mess/i.test(component.name));
      if (mess && !this.form.fee_component_id) this.form.fee_component_id = mess.id;
    },

    // Picking a month resets the dates to its first and last day.
    setDatesFromMonth(month) {
      if (!month) return;
      this.form.start_date = moment(month, "YYYY-MM").startOf("month").format("YYYY-MM-DD");
      this.form.end_date = moment(month, "YYYY-MM").endOf("month").format("YYYY-MM-DD");
    },

    async loadRecentBatches() {
      const url = this.$store.getters.getAPIKey.mainAPI + `/feeimports?type=mess`;
      const response = await this.$axios.get(url);
      this.recentBatches = response.data;
    },

    formatDate(value) {
      return value ? moment(value).format("DD-MM-YYYY HH:mm") : "-";
    },

    async revertBatch(batch) {
      if (!window.confirm(`Revert the import "${batch.file_name || batch.id}"? Students who haven't paid this bill will have it removed.`)) {
        return;
      }

      this.revertingBatchId = batch.id;
      try {
        const url = this.$store.getters.getAPIKey.mainAPI + `/feeimports/${batch.id}/revert`;
        const response = await this.$axios.post(url);
        this.$toast.success(
          `Reverted ${response.data.reverted_count} student(s).` +
          (response.data.skipped_count ? ` ${response.data.skipped_count} skipped - already paid.` : "")
        );
        await this.loadRecentBatches();
      } catch (e) {
        this.$toast.error(e.response?.data?.message || "Could not revert this import.");
      } finally {
        this.revertingBatchId = null;
      }
    },

    async onFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.fileName = file.name;
      try {
        this.rawRows = await readXlsxFile(file);
        this.headerRowCount = 1;
      } catch (e) {
        this.$toast.error("Could not read that file - " + e.message);
        this.rawRows = [];
      }
    },

    isBlankCell(value) {
      return value === null || value === undefined || String(value).trim() === "";
    },

    // One heading per column: the bottom-most non-blank header cell wins.
    headerFor(headerRows, col) {
      for (let r = headerRows.length - 1; r >= 0; r--) {
        const cell = headerRows[r][col];
        if (!this.isBlankCell(cell)) return String(cell).trim();
      }
      return null;
    },

    buildColumnMappings() {
      const headerRows = this.rawRows.slice(0, this.headerRowCount);
      const width = Math.max(...headerRows.map((row) => row.length));

      // Drop fully-empty rows (trailing blank lines Excel often leaves behind).
      this.dataRows = this.rawRows.slice(this.headerRowCount)
        .filter((row) => row.some((cell) => !this.isBlankCell(cell)));

      this.columnMappings = [];
      this.excludedColumns = [];

      for (let index = 0; index < width; index++) {
        const header = this.headerFor(headerRows, index) || `Column ${index + 1}`;
        const mapping = { index, header, type: this.guessColumnType(header) };

        // Only the ID and amount columns are listed - the rest start out excluded.
        if (mapping.type === "ignore") {
          this.excludedColumns.push(mapping);
        } else {
          this.columnMappings.push(mapping);
        }
      }

      this.mappingError = null;
      this.step = 2;
    },

    removeColumnMapping(mapping) {
      this.columnMappings = this.columnMappings.filter((m) => m.index !== mapping.index);
      this.excludedColumns.push(mapping);
    },

    restoreColumn(mapping) {
      this.excludedColumns = this.excludedColumns.filter((m) => m.index !== mapping.index);
      mapping.type = "amount";
      this.columnMappings.push(mapping);
    },

    // Mess sheets only need a student ID and an amount - everything else
    // (name, class, room, days, ...) is left out by default.
    guessColumnType(header) {
      const h = header.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
      if (/regist(er|ration)\s*(no|number|code)?|^reg\s*no/.test(h)) return "register_no";
      if (/admission\s*(no|number|code)?|^adm\s*no/.test(h)) return "admission_no";
      if (/amount|total|net|payable|bill/.test(h)) return "amount";
      return "ignore";
    },

    parseAmount(value) {
      if (value === null || value === undefined || value === "" || value === "-") return 0;
      if (typeof value === "number") return value;
      const parsed = parseFloat(String(value).replace(/,/g, "").trim());
      return isNaN(parsed) ? 0 : parsed;
    },

    validateMappings() {
      const counts = {};
      this.columnMappings.forEach((m) => {
        counts[m.type] = (counts[m.type] || 0) + 1;
      });

      if ((counts.register_no || 0) + (counts.admission_no || 0) !== 1) {
        return "Map exactly one column as Register No or Admission No.";
      }
      if ((counts.amount || 0) !== 1) {
        return "Map exactly one column as Amount.";
      }
      return null;
    },

    columnIndexOf(type) {
      const mapping = this.columnMappings.find((m) => m.type === type);
      return mapping ? mapping.index : null;
    },

    // One row per student - their ID (from whichever ID column was mapped)
    // and the amount.
    buildRequest(dryRun) {
      const identifierType = this.columnIndexOf("register_no") !== null ? "register_no" : "admission_no";
      const idIdx = this.columnIndexOf(identifierType);
      const amountIdx = this.columnIndexOf("amount");

      return {
        dry_run: dryRun,
        fee_component_id: this.form.fee_component_id,
        bill_name: this.form.bill_name,
        billing_frequency: this.form.billing_frequency,
        month: this.form.billing_frequency === "monthly" ? this.form.month : null,
        academic_year: ["semester", "yearly"].includes(this.form.billing_frequency) ? this.form.academic_year : null,
        semester: this.form.billing_frequency === "semester" ? this.form.semester : null,
        start_date: this.form.start_date,
        end_date: this.form.end_date,
        due_date: this.form.due_date || null,
        identifier_type: identifierType,
        rows: this.dataRows.map((row) => ({
          identifier: (row[idIdx] ?? "").toString().trim(),
          amount: this.parseAmount(row[amountIdx]),
        })),
      };
    },

    async runDryRun() {
      this.mappingError = this.validateMappings();
      if (this.mappingError) return;

      this.loading = true;
      try {
        const url = this.$store.getters.getAPIKey.mainAPI + `/feeimports/mess`;
        const response = await this.$axios.post(url, this.buildRequest(true));
        this.previewReport = response.data;
        this.importResult = null;
        this.step = 3;
      } catch (e) {
        this.$toast.error(e.response?.data?.message || "Could not preview this import.");
      } finally {
        this.loading = false;
      }
    },

    async confirmImport() {
      this.loading = true;
      try {
        const url = this.$store.getters.getAPIKey.mainAPI + `/feeimports/mess`;
        const response = await this.$axios.post(url, {
          ...this.buildRequest(false),
          file_name: this.fileName,
          column_mapping: this.columnMappings,
        });
        this.importResult = response.data;
        this.previewReport = response.data;
        this.$toast.success("Import complete");
        await this.loadRecentBatches();
      } catch (e) {
        this.$toast.error(e.response?.data?.message || "Import failed.");
      } finally {
        this.loading = false;
      }
    },

    statusBadgeClass(status) {
      return {
        badge: true,
        "badge-success": status === "ok",
        "badge-danger": status === "error",
      };
    },
  },
};
</script>
